import React, { useState, useEffect } from 'react';
import {
    Form, Button, Radio, Typography, message, notification,
    QRCode, Card, Tag, Space, Modal, Input,
} from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';

const { Text } = Typography;

export default function AdminSettings() {
    const [mfaEnabled, setMfaEnabled]           = useState(false);
    const [mfaMethod, setMfaMethod]             = useState(null);
    const [qrCode, setQrCode]                   = useState('');
    const [secret, setSecret]                   = useState('');
    const [otp, setOtp]                         = useState('');
    const [loading, setLoading]                 = useState(false);
    const [availableMethods, setAvailableMethods] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isDisableModalVisible, setIsDisableModalVisible] = useState(false);

    // Fetch active MFA methods, then auto-generate QR if authenticator is the only option
    useEffect(() => {
        adminApi.get('/admin/mfa/active-methods')
            .then((res) => {
                if (res.data.status === 'success') {
                    const methods = res.data.data.map((m) => m.name);
                    setAvailableMethods(methods);
                    if (methods.length > 0) setMfaMethod(methods[0]);
                }
            })
            .catch(() => message.error('Failed to load MFA methods.'));
    }, []);

    // Auto-generate QR code when authenticator is selected and MFA is not yet enabled
    useEffect(() => {
        if (mfaMethod === 'authenticator' && !mfaEnabled && !qrCode) {
            adminApi.post('/admin/mfa/generate-secret', { type: 'authenticator' })
                .then((res) => {
                    if (res.data.status === 'success') {
                        setQrCode(res.data.data.qr_code_url);
                        setSecret(res.data.data.secret);
                    }
                })
                .catch(() => message.error('Failed to generate QR code.'));
        }
    }, [mfaMethod, mfaEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    // Enable button logic
    useEffect(() => {
        const shouldDisable =
            !mfaMethod ||
            (mfaMethod === 'authenticator' && (!qrCode || !otp || otp.length !== 6));
        setIsButtonDisabled(shouldDisable);
    }, [mfaMethod, qrCode, otp]);

    // Fetch current MFA status
    useEffect(() => {
        adminApi.get('/admin/mfa/status')
            .then((res) => {
                if (res.data.status === 'success') {
                    const { mfa_enabled, mfa_type } = res.data.data;
                    setMfaEnabled(mfa_enabled);
                    if (mfa_type) setMfaMethod(mfa_type);
                }
            })
            .catch(() => message.error('Failed to fetch MFA status.'));
    }, []);

    const handleMethodChange = (e) => {
        setMfaMethod(e.target.value);
        setQrCode('');
        setSecret('');
        setOtp('');
        // QR generation is handled by the useEffect above
    };

    const handleEnableMfa = async () => {
        if (mfaMethod === 'authenticator' && (otp.length !== 6 || !secret)) {
            message.error('Please enter a valid 6-digit verification code.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                type: mfaMethod,
                ...(mfaMethod === 'authenticator' && { otp, secret }),
            };

            const res = await adminApi.post('/admin/mfa/enable', payload);
            if (res.status === 200 && res.data.status === 'success') {
                notification.success({ message: 'Settings Saved', description: 'MFA has been successfully enabled.' });
                setMfaEnabled(true);
                setQrCode('');
                setSecret('');
                setOtp('');
            } else {
                notification.error({ message: 'Failed to enable MFA', description: 'Please try again.' });
            }
        } catch {
            notification.error({ message: 'Failed to enable MFA', description: 'Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleDisableMfa = async () => {
        try {
            const res = await adminApi.post('/admin/mfa/disable');
            if (res.status === 200 && res.data.status === 'success') {
                notification.success({ message: 'Settings Saved', description: 'MFA disabled successfully.' });
                setMfaEnabled(false);
                setMfaMethod(availableMethods[0] || null);
                setQrCode('');
                setSecret('');
                setOtp('');
            } else {
                message.error('Failed to disable MFA. Please try again.');
            }
        } catch {
            message.error('An error occurred while disabling MFA.');
        } finally {
            setIsDisableModalVisible(false);
        }
    };

    return (
        <AdminLayout title="Settings">
            <div style={{ maxWidth: 680 }}>
                <Card
                    title={
                        <Space>
                            <LockOutlined />
                            <span>Multi-Factor Authentication (MFA)</span>
                        </Space>
                    }
                >
                    {mfaEnabled ? (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div style={{
                                padding: 16, backgroundColor: '#f6ffed',
                                border: '1px solid #b7eb8f', borderRadius: 8, marginBottom: 24,
                            }}>
                                <Space>
                                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                                    <Text strong>MFA is currently enabled</Text>
                                </Space>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                <Space wrap>
                                    <Text strong>Authentication Method:</Text>
                                    <Tag color="green" icon={<CheckCircleOutlined />}>
                                        {mfaMethod?.charAt(0).toUpperCase() + mfaMethod?.slice(1)}
                                    </Tag>
                                </Space>
                                <Button
                                    type="primary"
                                    danger
                                    onClick={() => setIsDisableModalVisible(true)}
                                    style={{ flexShrink: 0 }}
                                >
                                    Disable MFA
                                </Button>
                            </div>
                        </Space>
                    ) : (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div style={{ marginBottom: 24 }}>
                                <Text type="secondary">
                                    Multi-Factor Authentication adds an extra layer of security to your admin account.
                                    When enabled, you will need to provide a verification code in addition to your password when signing in.
                                </Text>
                            </div>

                            <div style={{ marginBottom: 24 }}>
                                <Text strong style={{ display: 'block', marginBottom: 16 }}>Select Authentication Method:</Text>
                                <Radio.Group
                                    onChange={handleMethodChange}
                                    value={mfaMethod}
                                    style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                                >
                                    {availableMethods.map((method) => (
                                        <Radio key={method} value={method} style={{ width: 'fit-content' }}>
                                            {method.charAt(0).toUpperCase() + method.slice(1)}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </div>

                            {mfaMethod === 'authenticator' && (
                                <>
                                    <div style={{
                                        marginBottom: 24, textAlign: 'center',
                                        padding: 24, backgroundColor: '#f5f5f5', borderRadius: 8,
                                    }}>
                                        {qrCode ? (
                                            <>
                                                <QRCode value={qrCode} size={200} />
                                                <p style={{ marginTop: 16, textAlign: 'left' }}>
                                                    Scan this QR code with your authenticator app, then enter the 6-digit code below to confirm setup.
                                                </p>
                                            </>
                                        ) : (
                                            <p>Generating QR code...</p>
                                        )}
                                    </div>

                                    <Form layout="vertical">
                                        <Form.Item
                                            label="Enter Verification Code"
                                            rules={[
                                                { required: true, message: 'Please enter the verification code from your authenticator app.' },
                                                { len: 6, message: 'Code must be exactly 6 digits.' },
                                            ]}
                                        >
                                            <Input.OTP
                                                value={otp}
                                                onChange={setOtp}
                                                length={6}
                                                isInputNum
                                                style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}
                                            />
                                        </Form.Item>
                                    </Form>
                                </>
                            )}

                            <div style={{ textAlign: 'right', marginTop: 24 }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    loading={loading}
                                    onClick={handleEnableMfa}
                                    disabled={isButtonDisabled}
                                    style={{
                                        background: 'var(--gradient-primary)',
                                        border: 'none',
                                        borderRadius: 'var(--radius-md)',
                                        fontWeight: 700,
                                    }}
                                >
                                    Enable MFA
                                </Button>
                            </div>
                        </Space>
                    )}
                </Card>
            </div>

            <Modal
                title="Disable Multi-Factor Authentication"
                open={isDisableModalVisible}
                onOk={handleDisableMfa}
                onCancel={() => setIsDisableModalVisible(false)}
                okText="Yes, Disable MFA"
                okType="danger"
                cancelText="Cancel"
                centered
            >
                <p>Are you sure you want to disable MFA? This will reduce the security of your admin account.</p>
            </Modal>
        </AdminLayout>
    );
}
